import { useMemo, useEffect, useState } from 'react';
import { useArticle } from '@backpackjs/storefront';
import handleize from '../utils/handleize';
import { ArticleTableOfContents } from '../snippets';

function ProgressBar(props) {
  const { scroll } = props;

  return (
    <>
      <div className="fixed top-[60px] z-[10] h-[4px] w-full bg-[#fff] lg:top-[83px]" />
      <div
        className="progress fixed top-[60px] z-[10] h-[4px] w-full lg:top-[83px]"
        style={{
          background: `linear-gradient(to right, #C1EFFA ${scroll}, #8DCEF9, transparent  0)`,
        }}
      />
    </>
  );
}

export default function ArticleTemplate({ article, renderSections }) {
  const settings = useArticle();
  const date = useMemo(() => {
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    return new Date(
      article.publishedAt || article.createdAt
    ).toLocaleDateString('en-US', options);
  }, [article.createdAt, article.publishedAt]);

  const [sectionTitles, setSectionTitles] = useState([]);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isOpen, setIsOpen] = useState(true);

  const filteredArticleHero = () => {
    return renderSections().filter(
      (item) =>
        item.props['data-comp'].includes('articleHero') ||
        item.props['data-comp'].includes('dynamicArticleHero')
    );
  };

  const filteredArticleSections = () => {
    return renderSections().filter(
      (item) =>
        item.props['data-comp'].includes('article') &&
        !(
          item.props['data-comp'].includes('articleHero') ||
          item.props['data-comp'].includes('dynamicArticleHero')
        )
    );
  };

  const filteredSections = () => {
    return renderSections().filter(
      (item) =>
        !item.props['data-comp'].includes('article') &&
        !item.props['data-comp'].includes('dynamicArticleHero')
    );
  };

  const getTableOfContents = () => {
    if (renderSections().length) {
      const sectionTitlesNew = [];

      filteredArticleSections().forEach((item) => {
        const sectionData = item.props.children.props.children.props.cms;

        if (sectionData?.anchorId && sectionData?.anchorId.length) {
          const sectionTitleData = {
            title: sectionData.anchorId,
            handle: handleize(sectionData.anchorId),
          };
          sectionTitlesNew.push(sectionTitleData);
        }
      });

      setSectionTitles(sectionTitlesNew);
    }
  };

  const scrollToView = (titleID) => {
    document.getElementById(titleID).scrollIntoView();
  };

  const scrollToViewMobile = (titleID) => {
    setIsOpen(!isOpen);
    document.getElementById(titleID).scrollIntoView();
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    getTableOfContents();
  }, []);

  const isInViewport = (element) => {
    try {
      if (!document?.querySelector(element)) return;
      const rect = document.querySelector(element).getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    } catch (e) {
      console.log(
        `There was an error determining if this ID "${element}" was in the viewport. Please ensure the ID you're using is valid`
      );
    }
  };

  const getDocHeight = () => {
    return Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    );
  };

  const calculateScrollDistance = () => {
    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const docHeight = getDocHeight();

    const totalDocScrollLength = docHeight - windowHeight;

    setScrollPosition(Math.floor((scrollTop / totalDocScrollLength) * 100));
  };

  const listenToScrollEvent = () => {
    document.addEventListener('scroll', () => {
      requestAnimationFrame(() => {
        calculateScrollDistance();
      });
    });
  };

  useEffect(() => {
    listenToScrollEvent();
  }, []);

  return (
    <>
      <ArticleTableOfContents
        titles={sectionTitles}
        scrollToView={scrollToViewMobile}
        isInViewport={isInViewport}
      />
      {!!filteredArticleHero().length && (
        <div className="lg:mb-8">{filteredArticleHero()}</div>
      )}
      <ProgressBar scroll={`${scrollPosition}%`} />
      <div className="py-contained px-contained text-primary">
        <div className="lg:flex">
          <div className="relative max-w-[290px] lg:w-1/5">
            <div className="relatvie sticky top-32 hidden lg:block">
              <button
                type="button"
                className="relative mb-8 flex w-full cursor-pointer border-b border-[#202635] pb-6  text-[16px] font-[400] uppercase tracking-[.04em]"
                onClick={() => toggleDropdown()}
              >
                <span>TABLE OF CONTENTS</span>
                <span
                  className={`absolute right-0 top-[12px] -translate-y-1/2 ${
                    isOpen ? 'true' : 'block rotate-180 transform'
                  }`}
                >
                  <svg
                    width="12"
                    height="6"
                    viewBox="0 0 12 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M6 0L0.803848 5.25H11.1962L6 0Z" fill="#202635" />
                  </svg>
                </span>
              </button>
              {isOpen &&
                sectionTitles.map((item, index) => {
                  return (
                    <button
                      type="button"
                      className={`group relative mb-6 w-full cursor-pointer border-b border-[#DDE2E7] pb-6 text-left font-[400] last:border-b-0 hover:text-[#202635] ${
                        isInViewport(`#${item.handle}`)
                          ? 'text-[#202635]'
                          : 'text-shadowGray'
                      }`}
                      key={index}
                      onClick={() => scrollToView(item.handle)}
                    >
                      <span
                        className={`absolute bottom-0 left-0 h-[1px] w-full bg-gradient-to-r from-[#F1DCC1] via-[#8DCEF9] to-[#2179C2] group-hover:opacity-100 ${
                          isInViewport(`#${item.handle}`)
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                      />
                      {item.title}
                    </button>
                  );
                })}
            </div>
          </div>
          <div className="max-w-[765px] lg:ml-20">
            {filteredArticleSections()}
          </div>
        </div>
      </div>
      <div>{filteredSections()}</div>
    </>
  );
}

ArticleTemplate.displayName = 'ArticleTemplate';
